import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import '../App.css';
import ExportButton from './ExportButton';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Use relative URL for API calls in Docker, fallback to localhost for local dev
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const OpenAIDashboard = () => {
  const history = useHistory();
  const [summary, setSummary] = useState(null);
  const [dailyMetrics, setDailyMetrics] = useState([]);
  const [userMetrics, setUserMetrics] = useState([]);
  const [modelMetrics, setModelMetrics] = useState([]);
  const [costTrends, setCostTrends] = useState([]);
  const [projectMetrics, setProjectMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [days, setDays] = useState(7);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch OpenAI-specific metrics (filtered by customer_type=openai)
      const [summaryRes, dailyRes, userRes, modelRes, costRes, projectRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/metrics/summary?days=${days}&customer_type=openai`),
        axios.get(`${API_BASE_URL}/metrics/daily?days=30&customer_type=openai`),
        axios.get(`${API_BASE_URL}/metrics/by-user?days=${days}&customer_type=openai`),
        axios.get(`${API_BASE_URL}/metrics/by-model?days=${days}&customer_type=openai`),
        axios.get(`${API_BASE_URL}/metrics/cost-trends?days=30&customer_type=openai`),
        axios.get(`${API_BASE_URL}/metrics/by-workspace?days=${days}&customer_type=openai`),
      ]);

      setSummary(summaryRes.data);
      setDailyMetrics(dailyRes.data.data);
      setUserMetrics(userRes.data.data);
      setModelMetrics(modelRes.data.data);
      setCostTrends(costRes.data.data);
      setProjectMetrics(projectRes.data.data);

      // Log the data for debugging
      console.log("OpenAI summary data:", summaryRes?.data);
      console.log("OpenAI daily metrics:", dailyRes?.data?.data);
      console.log("OpenAI user metrics:", userRes?.data?.data);
      console.log("OpenAI model metrics:", modelRes?.data?.data);
      console.log("OpenAI cost trends:", costRes?.data?.data);
      console.log("OpenAI project metrics:", projectRes?.data?.data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching OpenAI data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncStatus({ type: 'info', message: 'Syncing OpenAI data...' });

      await axios.post(`${API_BASE_URL}/sync?days=30`, {});

      // Wait for sync to complete
      setTimeout(async () => {
        try {
          // Fetch updated summary to check for data
          const summaryRes = await axios.get(`${API_BASE_URL}/metrics/summary?days=7&customer_type=openai`);
          const recordCount = summaryRes.data.total_sessions || 0;

          setLastSyncTime(new Date());

          if (recordCount === 0) {
            setSyncStatus({
              type: 'warning',
              message: `Sync completed, but no OpenAI usage data found. This could mean: (1) No OpenAI API usage yet, (2) API key not configured, or (3) Usage data not yet available.`,
              details: `Synced at: ${new Date().toLocaleString()}`
            });
          } else {
            setSyncStatus({
              type: 'success',
              message: `Sync completed successfully! Found ${recordCount} sessions.`,
              details: `Synced at: ${new Date().toLocaleString()}`
            });
          }

          await fetchData();
        } catch (err) {
          setSyncStatus({
            type: 'error',
            message: `Sync initiated but failed to fetch results: ${err.message}`,
            details: err.response?.data ? JSON.stringify(err.response.data) : null
          });
        } finally {
          setSyncing(false);
        }
      }, 5000);
    } catch (err) {
      setSyncing(false);
      setSyncStatus({
        type: 'error',
        message: `Failed to start sync: ${err.message}`,
        details: err.response?.data ? JSON.stringify(err.response.data) : null
      });
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [days]);

  if (loading) {
    return (
      <div className="App">
        <div className="loading">Loading OpenAI Dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const tokenTrendData = {
    labels: dailyMetrics.map((m) => m.date),
    datasets: [
      {
        label: 'Input Tokens',
        data: dailyMetrics.map((m) => m.input_tokens),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
      },
      {
        label: 'Output Tokens',
        data: dailyMetrics.map((m) => m.output_tokens),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
      },
      {
        label: 'Cached Tokens',
        data: dailyMetrics.map((m) => m.cache_read_tokens || 0),
        borderColor: 'rgb(255, 205, 86)',
        backgroundColor: 'rgba(255, 205, 86, 0.2)',
      },
    ],
  };

  const costTrendData = {
    labels: costTrends.map((m) => m.date),
    datasets: [
      {
        label: 'Cost (USD)',
        data: costTrends.map((m) => m.cost_usd),
        borderColor: 'rgb(16, 163, 127)',
        backgroundColor: 'rgba(16, 163, 127, 0.2)',
        fill: true,
      },
    ],
  };

  const userCostData = {
    labels: userMetrics.map((m) => m.actor),
    datasets: [
      {
        label: 'Cost (USD)',
        data: userMetrics.map((m) => m.cost_usd),
        backgroundColor: 'rgba(16, 163, 127, 0.6)',
      },
    ],
  };

  const modelCostData = {
    labels: modelMetrics.map((m) => m.model),
    datasets: [
      {
        label: 'Cost (USD)',
        data: modelMetrics.map((m) => m.cost_usd),
        backgroundColor: [
          'rgba(16, 163, 127, 0.8)',
          'rgba(52, 211, 153, 0.8)',
          'rgba(134, 239, 172, 0.8)',
          'rgba(74, 222, 128, 0.8)',
          'rgba(34, 197, 94, 0.8)',
        ],
      },
    ],
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-left">
          <button onClick={() => history.push('/main_app')} className="back-btn" title="Back to Dashboard Selection">
            ← Back
          </button>
          <h1>
            <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>🤖</span>
            OpenAI Usage Dashboard
          </h1>
        </div>
        <div className="header-controls">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="days-selector"
            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <div className="export-buttons">
            <ExportButton exportType="summary" days={days} label="Export Summary" />
            <ExportButton exportType="daily" days={30} label="Export Daily" />
            <ExportButton exportType="by-workspace" days={days} label="Export Projects" />
          </div>
          <button onClick={handleSync} disabled={syncing} className="sync-btn">
            {syncing ? 'Syncing...' : 'Sync Data'}
          </button>

        </div>
      </header>

      {summary && (
        <div className="summary-cards">
          <div className="card">
            <h3>Total Cost</h3>
            <p className="metric-value">${summary.total_cost_usd.toFixed(2)}</p>
            <p className="metric-label">Last {days} days</p>
          </div>
          <div className="card">
            <h3>Total Tokens</h3>
            <p className="metric-value">
              {(summary.total_input_tokens + summary.total_output_tokens).toLocaleString()}
            </p>
            <p className="metric-label">Input + Output</p>
          </div>
          <div className="card">
            <h3>API Requests</h3>
            <p className="metric-value">{summary.total_sessions.toLocaleString()}</p>
            <p className="metric-label">Last {days} days</p>
          </div>
          <div className="card">
            <h3>Active API Keys</h3>
            <p className="metric-value">{summary.unique_users}</p>
            <p className="metric-label">Unique keys used</p>
          </div>
          <div className="card">
            <h3>Input Tokens</h3>
            <p className="metric-value">{summary.total_input_tokens.toLocaleString()}</p>
            <p className="metric-label">Prompt tokens</p>
          </div>
          <div className="card">
            <h3>Output Tokens</h3>
            <p className="metric-value">{summary.total_output_tokens.toLocaleString()}</p>
            <p className="metric-label">Completion tokens</p>
          </div>
          <div className="card">
            <h3>Cached Tokens</h3>
            <p className="metric-value">{(summary.total_cache_read_tokens || 0).toLocaleString()}</p>
            <p className="metric-label">Cache reads</p>
          </div>
          <div className="card">
            <h3>Avg Cost/Request</h3>
            <p className="metric-value">
              ${summary.total_sessions > 0 ? (summary.total_cost_usd / summary.total_sessions).toFixed(4) : '0.00'}
            </p>
            <p className="metric-label">Per API call</p>
          </div>
        </div>
      )}

      <div className="charts-container">
        <div className="chart-card">
          <h2>Token Usage Trends (30 Days)</h2>
          <Line data={tokenTrendData} options={{ responsive: true, maintainAspectRatio: true }} />
        </div>

        <div className="chart-card">
          <h2>Cost Trends (30 Days)</h2>
          <Line
            data={costTrendData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: function (value) {
                      return '$' + value.toFixed(2);
                    },
                  },
                },
              },
            }}
          />
        </div>

        <div className="chart-card">
          <h2>Cost by API Key (Last {days} Days)</h2>
          <Bar
            data={userCostData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: function (value) {
                      return '$' + value.toFixed(2);
                    },
                  },
                },
              },
            }}
          />
        </div>

        <div className="chart-card">
          <h2>Cost by Model (Last {days} Days)</h2>
          <Doughnut
            data={modelCostData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: {
                  position: 'right',
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      return context.label + ': $' + context.parsed.toFixed(2);
                    },
                  },
                },
              },
            }}
          />
        </div>

        <div className="chart-card full-width">
          <h2>Usage by Project (Last {days} Days)</h2>
          {projectMetrics.length > 0 ? (
            <div className="table-container">
              <table className="metrics-table">
                <thead>
                  <tr>
                    <th>Project ID</th>
                    <th>Sessions</th>
                    <th>Input Tokens</th>
                    <th>Output Tokens</th>
                    <th>Cached Tokens</th>
                    <th>Unique Keys</th>
                    <th>Cost (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {projectMetrics.map((project, index) => (
                    <tr key={index}>
                      <td>
                        <strong>{project.workspace_id || 'Unknown Project'}</strong>
                      </td>
                      <td>{project.sessions.toLocaleString()}</td>
                      <td>{project.input_tokens.toLocaleString()}</td>
                      <td>{project.output_tokens.toLocaleString()}</td>
                      <td>{(project.cache_read_tokens || 0).toLocaleString()}</td>
                      <td>{project.unique_users}</td>
                      <td>
                        <strong>${project.cost_usd.toFixed(2)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <p>No project data available yet.</p>
              <p style={{ fontSize: '0.9em', color: '#666' }}>
                Sync your OpenAI usage data to populate this section.
              </p>
            </div>
          )}
        </div>

        <div className="chart-card full-width">
          <h2>Usage by API Key (Last {days} Days)</h2>
          {userMetrics.length > 0 ? (
            <div className="table-container">
              <table className="metrics-table">
                <thead>
                  <tr>
                    <th>API Key ID</th>
                    <th>Sessions</th>
                    <th>Input Tokens</th>
                    <th>Output Tokens</th>
                    <th>Cached Tokens</th>
                    <th>Cost (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {userMetrics.map((user, index) => (
                    <tr key={index}>
                      <td>
                        <strong>{user.actor}</strong>
                      </td>
                      <td>{user.sessions.toLocaleString()}</td>
                      <td>{user.input_tokens.toLocaleString()}</td>
                      <td>{user.output_tokens.toLocaleString()}</td>
                      <td>{(user.cache_read_tokens || 0).toLocaleString()}</td>
                      <td>
                        <strong>${user.cost_usd.toFixed(2)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <p>No API key usage data available yet.</p>
              <p style={{ fontSize: '0.9em', color: '#666' }}>
                Sync your OpenAI data to populate this section.
              </p>
            </div>
          )}
        </div>

        <div className="chart-card full-width">
          <h2>Model Usage Details (Last {days} Days)</h2>
          {modelMetrics.length > 0 ? (
            <div className="table-container">
              <table className="metrics-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Sessions</th>
                    <th>Input Tokens</th>
                    <th>Output Tokens</th>
                    <th>Total Tokens</th>
                    <th>Cost (USD)</th>
                    <th>Avg Cost/Request</th>
                  </tr>
                </thead>
                <tbody>
                  {modelMetrics.map((model, index) => (
                    <tr key={index}>
                      <td>
                        <strong>{model.model}</strong>
                      </td>
                      <td>{model.sessions.toLocaleString()}</td>
                      <td>{model.input_tokens.toLocaleString()}</td>
                      <td>{model.output_tokens.toLocaleString()}</td>
                      <td>{(model.input_tokens + model.output_tokens).toLocaleString()}</td>
                      <td>
                        <strong>${model.cost_usd.toFixed(2)}</strong>
                      </td>
                      <td>
                        ${model.sessions > 0 ? (model.cost_usd / model.sessions).toFixed(4) : '0.00'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <p>No model usage data available yet.</p>
              <p style={{ fontSize: '0.9em', color: '#666' }}>
                Sync your OpenAI data to populate this section.
              </p>
            </div>
          )}
        </div>
      </div>

      {syncStatus && (
        <div className={`status-footer status-${syncStatus.type}`}>
          <div className="status-content">
            <div className="status-icon">
              {syncStatus.type === 'success' && '✓'}
              {syncStatus.type === 'error' && '✗'}
              {syncStatus.type === 'warning' && '⚠'}
              {syncStatus.type === 'info' && 'ℹ'}
            </div>
            <div className="status-message">
              <strong>{syncStatus.message}</strong>
              {syncStatus.details && <div className="status-details">{syncStatus.details}</div>}
            </div>
            <button className="status-close" onClick={() => setSyncStatus(null)}>×</button>
          </div>
        </div>
      )}

      <footer className="footer">
        <p>
          Data refreshed every 5 minutes | Last updated: {new Date().toLocaleString()}
          {lastSyncTime && ` | Last sync: ${lastSyncTime.toLocaleString()}`}
        </p>
      </footer>
    </div>
  );
};

export default OpenAIDashboard;
